import { useLazyQuery } from '@apollo/client';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import { map } from 'lodash';
import toUpper from 'lodash/toUpper';
import { useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CACHE_REVALIDATION_TIME,
  MATOMO_CATEGORY,
  PAGE_TYPE,
  SESSION_STATUS,
  TRACK_VALUE
} from '../../../common/constants';
import useWatchTime from '../../../common/useWatchTime';
import MODULES, {
  getMetaOgImage,
  getUniqueVideoPodcastIds
} from '../../../common/utils';
import CustomMeta from '../../../components/CustomMeta';
import ModuleComponent from '../../../components/ModuleComponents';
import { GET_PAGE_V2, PUBLISHED_PAGES } from '../../../graphql/Queries';
import client from '../../../lib/apollo';
import { gaEvent } from '../../../lib/gtag';
import { useApp } from '../../../providers/AppContext';

const MetaFooter = dynamic(() => import('../../../components/MetaFooter'), {
  loading: () => null,
  ssr: false
});

function Video({
  page: {
    modules: pageData = [],
    title,
    description,
    metaImage,
    metaHeader,
    metaFooter,
    slug,
    id: pageId,
    type
  } = {}
}) {
  const {
    state: { currentUser, loading }
  } = useApp();
  const { trackEvent } = useMatomo();
  const { data, status } = useSession();
  const [modules, setModules] = useState(() =>
    pageData?.map((module) => ({
      ...MODULES[module?.type]?.(module),
      isLoading: !!module?.showSkeleton
    }))
  );

  const [fetchPage] = useLazyQuery(GET_PAGE_V2, {
    fetchPolicy: 'no-cache',
    context: {
      headers: {
        token: data?.accessToken
      }
    },
    onCompleted: (page) => {
      if (page) {
        const moduleObject = page?.pageV2?.modules?.reduce((acc, module) => {
          acc[module?.id] = {
            ...MODULES[module?.type]?.(module)
          };
          return acc;
        }, {});
        setModules((prev) =>
          prev
            .filter(({ id, isLoading }) => !(isLoading && !moduleObject?.[id]))
            .map((module) => ({
              ...module,
              ...moduleObject?.[module?.id],
              isLoading: false
            }))
        );
      }
    },
    onError: () => {}
  });

  const permissionBasedModulesIds = useMemo(
    () =>
      pageData
        ?.filter((module) => !!module?.showSkeleton)
        ?.map((module) => module?.id),
    [pageData]
  );

  useEffect(() => {
    setModules(
      pageData?.map((module) => {
        return {
          ...MODULES[module?.type]?.(module),
          isLoading: !!module?.showSkeleton
        };
      })
    );
  }, [pageData]);

  useEffect(() => {
    if (loading) return;

    const eventData = {
      action: `VIEW_${toUpper(type)}`,
      email: currentUser?.email ?? '',
      instanceData: {
        id: pageId,
        slug,
        tags: modules?.[0]?.data?.instanceData?.tags,
        topics: modules?.[0]?.data?.instanceData?.topics,
        title
      },
      instanceId: pageId,
      instanceType: type,
      userId: currentUser?.id ?? ''
    };

    trackEvent({
      category: MATOMO_CATEGORY.VIEW_DETAIL,
      action: eventData?.action,
      name: JSON.stringify(eventData),
      value: TRACK_VALUE.SUCCESS
    });
    const gaEventData = {
      ...eventData
    };

    // Remove the email field from gaEventData
    delete gaEventData.email;
    gaEvent({
      category: MATOMO_CATEGORY.VIEW_DETAIL,
      action: gaEventData?.action,
      name: JSON.stringify(gaEventData),
      value: TRACK_VALUE.SUCCESS
    });
  }, [currentUser, loading, type, pageId, slug, title, modules, trackEvent]);

  useEffect(() => {
    if (
      status !== SESSION_STATUS.LOADING &&
      permissionBasedModulesIds.length > 0
    ) {
      setModules(
        pageData?.map((module) => ({
          ...MODULES[module?.type]?.(module),
          isLoading: !!module?.showSkeleton
        }))
      );
      fetchPage({
        variables: {
          where: {
            type: PAGE_TYPE?.PODCAST,
            slug
          },
          filter: {
            moduleIds: permissionBasedModulesIds
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, permissionBasedModulesIds, fetchPage, pageData, slug]);

  const { videoIds, podcastIds } = useMemo(
    () => getUniqueVideoPodcastIds(modules),
    [modules]
  );
  const { videosWatchTimes, podcastListenTimes } = useWatchTime(
    videoIds,
    podcastIds
  );
  const meta = metaHeader || '';

  return (
    <div>
      <CustomMeta
        title={title}
        description={description}
        image={metaImage || getMetaOgImage('videos', slug)}
        meta={meta}
      />
      {modules?.map((module) => (
        <ModuleComponent
          key={module?.id}
          videosWatchTimes={videosWatchTimes}
          podcastListenTimes={podcastListenTimes}
          {...module}
        />
      ))}
      <MetaFooter data={metaFooter} />
    </div>
  );
}

export async function getStaticPaths() {
  try {
    const res = await client.query({
      query: PUBLISHED_PAGES,
      fetchPolicy: 'no-cache',
      variables: {
        where: {
          type: PAGE_TYPE?.PODCAST
        }
      }
    });
    const slugs = res?.data?.publishedPages?.slugs;

    if (!slugs) return { paths: [], fallback: 'blocking' };

    return {
      paths:
        map(slugs, (slug) => {
          return { params: { slug } };
        }) || [],
      fallback: 'blocking'
    };
  } catch (error) {
    return { paths: [], fallback: 'blocking' };
  }
}

export async function getStaticProps(ctx) {
  const configs = {
    revalidate: CACHE_REVALIDATION_TIME
  };

  try {
    const {
      data: { pageV2: page }
    } = await client.query({
      query: GET_PAGE_V2,
      fetchPolicy: 'no-cache',
      variables: {
        where: {
          type: PAGE_TYPE?.PODCAST,
          slug: ctx?.params?.slug?.toLowerCase()
        },
        filter: {
          skipPermissionModules: true
        }
      },
      context: {
        headers: {
          'rate-limit-bypass-secret': process.env.RATE_LIMIT_BYPASS_SECRET
        }
      }
    });
    if (!page)
      return {
        notFound: true,
        ...configs
      };
    return {
      props: { page },
      ...configs
    };
  } catch (error) {
    return {
      notFound: true,
      ...configs
    };
  }
}

export default Video;
